import React, { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import { SuitcaseIcon, QuestionIcon, HandCoinsIcon } from "@phosphor-icons/react";
import {
	LOAN_PURPOSE_MERCHANT_CASH_ADVANCE,
	LOAN_PURPOSE_BUSINESS_TERM_ADVANCE,
	LOAN_PURPOSE_UNSURE,
	emailTypos,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import ToggleSwitch from "@/components/ui/toggleSwitch";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import { secureInput } from "@/lib/security";

export const PersonalDetails: React.FC = () => {
	// Get values and setters from Zustand store
	const { formValues, setFormValue, setFormError, formErrors } = useFormDataStore();
	const {
		selectedIndex,
		loanAmountMin,
		loanAmountMax,
		setValidateFormDetails,
		formContainer,
		setFormErrors,
		toggleTC,
		setToggleTC,
		hasAttemptedSubmit,
		enabledFields,
	} = useFormStateStore();

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		firstName: false,
		lastName: false,
		email: false,
		loanAmount: false,
		tc: false,
		loanPurpose: false,
	});

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	// Toggle Switch for T&C
	const [enabledTC, setEnabledTC] = useState(toggleTC);

	useEffect(() => {
		setToggleTC(enabledTC);
		if (errors.tc) {
			setErrors((prev) => ({ ...prev, tc: !enabledTC }));
		}
	}, [enabledTC]);

	const { firstName, lastName, email, loanAmount, loanPurpose } = formValues;
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = 430;
		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	// Email Typo Validation Helper
	const getEmailValidationError = (emailValue: string): string => {
		const trimmed = emailValue.trim();
		if (trimmed === "") return "Email is required";

		const sanitizedValue = secureInput.email(trimmed);
		if (!sanitizedValue.isValid) return "Email is not correctly formatted";

		const fullValue = trimmed.toLowerCase();
		const parts = fullValue.split("@");

		if (parts.length === 2) {
			const [username, domain] = parts;
			const typoRegex = /\.(con\.au|cm\.au|con|cm|co)$/i;
			const endTypoRegex = /\.com[a-z]$/;

			if (emailTypos[domain]) {
				return `Did you mean ${username}@${emailTypos[domain]}?`;
			} else if (typoRegex.test(domain)) {
				const fixedDomain = domain.replace(typoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			} else if (endTypoRegex.test(domain)) {
				const fixedDomain = domain.replace(endTypoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			}
		}
		return "";
	};

	// Handle input changes with validation
	const handleFirstNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("firstName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, firstName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleLastNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("lastName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, lastName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.email(value);
		setFormValue("email", sanitizedValue.sanitized);
		const emailErrorMsg = getEmailValidationError(sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, email: emailErrorMsg !== "" }));
		setFormError("email", emailErrorMsg);
	};

	const handleBlur = (field: "firstName" | "lastName" | "email", value: string) => {
		if (!value) return;
		if (field === "email") {
			setFormValue("email", value.toLowerCase());
		} else {
			// Capitalise first character, and convert ALL-CAPS strings to Capitalised
			const forcedCasing =
				value === value.toUpperCase()
					? value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
					: value.charAt(0).toUpperCase() + value.slice(1);
			setFormValue(field, forcedCasing);
		}
	};

	const handleLoanAmountChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			const cappedValue = Math.min(formattedValue, 500000);
			setFormValue("loanAmount", cappedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				loanAmount: cappedValue < loanAmountMin || cappedValue > loanAmountMax,
			}));
		},
		[setFormValue, loanAmountMin],
	);

	const handleLoanPurposeChange = useCallback(
		(value: string) => {
			setFormValue("loanPurpose", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				loanPurpose: !value,
			}));
		},
		[setFormValue],
	);

	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const emailErrorMsg = getEmailValidationError(email);

		const newErrors = {
			firstName: !secureInput.name(firstName).isValid,
			lastName: !secureInput.name(lastName).isValid,
			email: emailErrorMsg !== "",
			loanAmount: (() => {
				const amount = parseFloat(loanAmount.replace(/,/g, ""));

				return !amount || amount < loanAmountMin || amount > loanAmountMax;
			})(),
			tc: !enabledTC,
			loanPurpose: false,
		};

		setErrors(newErrors);
		setFormError("email", emailErrorMsg);

		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError;
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [firstName, lastName, email, loanAmount, enabledTC, loanPurpose]);

	return (
		<div className="jF-space-y-3">
			<div>
				<label
					htmlFor="firstName"
					className="jF-text-base jF-font-semibold jF-leading-6 jF-text-gray-900"
				>
					Contact Details
				</label>
				<div
					className={clsx(
						isNavDivSmall
							? "jF-flex-col jF-gap-x-2 jF-gap-y-1"
							: "jF-flex-row jF-gap-x-2",
						"jF-flex jF-mt-1",
					)}
				>
					<div className="jF-flex-1">
						<div className="jF-relative jF-mt-1">
							<input
								name="firstName"
								value={firstName}
								onChange={handleFirstNameChange}
								onBlur={(e) => handleBlur("firstName", e.target.value)}
								onFocus={() => handleFocus("firstName")}
								aria-label="First Name"
								type="text"
								id="firstName"
								placeholder="First Name"
								maxLength={30}
								autoComplete="given-name"
								className={clsx(
									"jF-capitalize",
									hasAttemptedSubmit && errors.firstName
										? "jF-ring-red-500 focus:jF-ring-red-500"
										: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
									"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
								)}
							/>
						</div>
						{hasAttemptedSubmit && errors.firstName && (
							<span className="jF-text-xs jF-text-red-500">First name required</span>
						)}
					</div>
					<div className="jF-flex-1">
						<div className="jF-relative jF-mt-1">
							<input
								name="lastName"
								value={lastName}
								onChange={handleLastNameChange}
								onBlur={(e) => handleBlur("lastName", e.target.value)}
								onFocus={() => handleFocus("lastName")}
								aria-label="Last Name"
								type="text"
								id="lastName"
								placeholder="Last Name"
								maxLength={30}
								autoComplete="family-name"
								className={clsx(
									"jF-capitalize",
									hasAttemptedSubmit && errors.lastName
										? "jF-ring-red-500 focus:jF-ring-red-500"
										: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
									"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
								)}
							/>
						</div>
						{hasAttemptedSubmit && errors.lastName && (
							<span className="jF-text-xs jF-text-red-500">
								Last name is required
							</span>
						)}
					</div>
				</div>

				<div className="jF-relative jF-mt-2">
					<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
						<EnvelopeIcon
							className="jF-h-5 jF-w-5 jF-text-gray-400"
							aria-hidden="true"
						/>
					</div>
					<input
						value={email}
						onChange={handleEmailChange}
						onBlur={(e) => handleBlur("email", e.target.value)}
						onFocus={() => {
							handleFocus("email");
							setFormError("email", "");
						}}
						aria-label="Email Address"
						type="email"
						id="email"
						placeholder="Email Address"
						maxLength={50}
						autoComplete="email"
						className={clsx(
							(hasAttemptedSubmit && errors.email) || formErrors.email
								? "jF-ring-red-500 focus:jF-ring-red-500"
								: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
							"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-pl-10 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 focus:jF-ring-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6 jF-bg-none",
						)}
					/>
				</div>
				{formErrors.email && (
					<span className="jF-text-xs jF-text-red-500">{formErrors.email}</span>
				)}
			</div>

			{/* Loan Types */}
			<RadioGroupField
				id="radioLoanPurpose"
				label="Loan Purpose"
				value={loanPurpose || ""}
				options={(enabledFields.loanPurpose ?? []).map((item) => ({
					id: item.id,
					value: item.value,
					label: item.value,
					icon:
						item.value === LOAN_PURPOSE_BUSINESS_TERM_ADVANCE ? (
							<SuitcaseIcon size={24} weight="duotone" />
						) : item.value == LOAN_PURPOSE_MERCHANT_CASH_ADVANCE ? (
							<HandCoinsIcon size={24} weight="duotone" />
						) : item.value == LOAN_PURPOSE_UNSURE ? (
							<QuestionIcon size={24} weight="duotone" />
						) : null,
				}))}
				onChange={handleLoanPurposeChange}
				required={true}
				error={errors.loanPurpose ? "This field is required" : ""}
				disabled={false}
				className={""}
				gridCols={3}
				showIcons={true}
				disabledOptions={[]}
				navRef={isNavDivSmall}
			/>
			<div
				className={clsx(
					isNavDivSmall
						? "jF-flex-col jF-gap-x-2  jF-space-y-2"
						: "jF-flex-row jF-gap-x-2",
					"jF-flex",
				)}
			>
				<div className="jF-flex-1">
					<label
						htmlFor="loan-amount"
						className="jF-text-base jF-font-semibold jF-leading-6 jF-text-gray-900"
					>
						Loan Amount
					</label>
					<div className="jF-relative jF-mt-1">
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
							<span className="jF-text-gray-500 sm:jF-text-md">$</span>
						</div>
						<input
							value={loanAmount}
							onChange={handleLoanAmountChange}
							onFocus={() => handleFocus("loanAmount")}
							aria-label="Loan Amount"
							placeholder={
								loanAmountMin > 0
									? `Loan Amount (Min ${loanAmountMin / 1000}K)`
									: ``
							}
							maxLength={7}
							className={clsx(
								hasAttemptedSubmit && errors.loanAmount
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
								"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-pl-7 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-gray-300 focus:jF-ring-1 jF-outline-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
							)}
							type="text"
							id="loan-amount"
							aria-describedby="loan-amount"
							inputMode="numeric"
						/>
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-right-0 jF-flex jF-items-center jF-pr-3">
							<span className="jF-text-gray-500 sm:jF-text-md">AUD</span>
						</div>
					</div>
					{hasAttemptedSubmit && errors.loanAmount && (
						<span className="jF-text-xs jF-text-red-500">
							Loan amount must be between ${loanAmountMin.toLocaleString()} & $
							{loanAmountMax.toLocaleString()}
						</span>
					)}
				</div>
			</div>

			<div id="tc-toggle" className={clsx("!jF-mt-4")} tabIndex={-1}>
				<ToggleSwitch label="" enabled={enabledTC} setEnabled={setEnabledTC} />
				{hasAttemptedSubmit && errors.tc && (
					<span className="jF-block jF-mt-1 jF-text-xs jF-text-red-500">
						Please agree to the terms and conditions to continue
					</span>
				)}
			</div>
		</div>
	);
};